import { CSSProperties, useCallback, useEffect, useRef } from 'react';

import swipeHandIcon from 'assets/images/icon/swipe-hand.svg';

import styles from './ParticleSphere.module.css';

export type ResilienceLevel = 'low' | 'borderline' | 'typical' | 'high';

type ParticleSphereProps = {
  level: ResilienceLevel;
  /** When false, blocks pointer interaction and hides the hint. Default true. */
  interactive?: boolean;
  /** Animated swipe hint overlay. Defaults to whatever `interactive` is. */
  showHint?: boolean;
  colorblind?: boolean;
  /** Render a single static frame instead of running the animation loop. */
  paused?: boolean;
  /** Scales the sphere within its frame to crop buffer whitespace. Default 1. */
  zoom?: number;
  className?: string;
};

type Rgb = readonly [number, number, number];

type Palette = {
  healthy: string[];
  unhealthy: string[];
  accent: string[];
  neutral: string[];
};

const PALETTES: Record<'normal' | 'colorblind', Palette> = {
  normal: {
    healthy: ['#4CAF50', '#66BB6A', '#2E7D32', '#81C784'],
    unhealthy: ['#E53935', '#C62828', '#EF5350', '#B71C1C'],
    accent: ['#FFA726', '#FF7043', '#FFB300', '#F4511E'],
    neutral: ['#9E9E9E', '#757575', '#BDBDBD', '#616161'],
  },
  colorblind: {
    healthy: ['#648FFF', '#4B74E0', '#7DA4FF', '#3558CC'],
    unhealthy: ['#FE6100', '#CC4E00', '#FF8533', '#B34400'],
    accent: ['#FFB000', '#DC9700', '#FFCA3A', '#B37D00'],
    neutral: ['#785EF0', '#5B43C4', '#9478F5', '#3D29A0'],
  },
};

type LevelConfig = {
  label: string;
  value: number;
  greenFrac: number;
  warpAmp: number;
  warpSpeed: number;
  rotSpeed: number;
  rotJitter: number;
  breathAmp: number;
  breathSpeed: number;
  shimmerStrength: number;
  driftAmp: number;
  autoShedRate: number;
  maxShed: number;
  sizeBase: number;
  sizeVar: number;
  alphaBase: number;
  alphaVar: number;
  physics: {
    spring: number;
    damping: number;
    maxDisp: number;
    brushInfl: number;
    partDecay: number;
  };
};

const LEVELS: Record<ResilienceLevel, LevelConfig> = {
  high: {
    label: 'High', value: 92, greenFrac: 0.95,
    warpAmp: 0, warpSpeed: 0.06, rotSpeed: 0.0018, rotJitter: 0,
    breathAmp: 0.010, breathSpeed: 0.5, shimmerStrength: 1.0,
    driftAmp: 0, autoShedRate: 0, maxShed: 2,
    sizeBase: 2.2, sizeVar: 0, alphaBase: 0.85, alphaVar: 0,
    physics: { spring: 0.30, damping: 0.52, maxDisp: 28, brushInfl: 0.55, partDecay: 0.50 },
  },
  typical: {
    label: 'Typical', value: 65, greenFrac: 0.85,
    warpAmp: 0.12, warpSpeed: 0.14, rotSpeed: 0.0032, rotJitter: 0,
    breathAmp: 0.018, breathSpeed: 0.8, shimmerStrength: 0.55,
    driftAmp: 0.4, autoShedRate: 0, maxShed: 6,
    sizeBase: 2.1, sizeVar: 0.2, alphaBase: 0.82, alphaVar: 0.04,
    physics: { spring: 0.06, damping: 0.76, maxDisp: 55, brushInfl: 0.70, partDecay: 0.93 },
  },
  borderline: {
    label: 'Borderline', value: 30, greenFrac: 0.55,
    warpAmp: 0.60, warpSpeed: 0.12, rotSpeed: 0.0055, rotJitter: 0.0012,
    breathAmp: 0, breathSpeed: 0, shimmerStrength: 0,
    driftAmp: 0.6, autoShedRate: 0.0008, maxShed: 15,
    sizeBase: 1.8, sizeVar: 2.8, alphaBase: 0.75, alphaVar: 0.25,
    physics: { spring: 0.0008, damping: 0.95, maxDisp: 80, brushInfl: 0.85, partDecay: 0.9993 },
  },
  low: {
    label: 'Low', value: 8, greenFrac: 0.20,
    warpAmp: 1.0, warpSpeed: 0.22, rotSpeed: 0.0085, rotJitter: 0.004,
    breathAmp: 0, breathSpeed: 0, shimmerStrength: 0,
    driftAmp: 1.2, autoShedRate: 0.0020, maxShed: 40,
    sizeBase: 1.5, sizeVar: 5.0, alphaBase: 0.70, alphaVar: 0.30,
    physics: { spring: 0.00005, damping: 0.97, maxDisp: 140, brushInfl: 1.20, partDecay: 0.9997 },
  },
};

type Particle = {
  nx: number; ny: number; nz: number;
  size: number;
  baseAlpha: number;
  alpha: number;
  /** 0 = morphable healthy/unhealthy, 1 = accent, 2 = neutral */
  type: 0 | 1 | 2;
  redColor?: Rgb;
  greenColor?: Rgb;
  fixedColor?: Rgb;
  currentT: number;
  animT: number;
  noiseOffset: [number, number, number];
  wobbleOffset: [number, number, number];
  driftOffset: [number, number, number];
  shimmerPhase: number;
  dx: number; dy: number; vx: number; vy: number;
  partDx: number; partDy: number;
  escapeCooldown: number;
  shed: boolean;
  regenScale: number;
  instability: number;
};

type EscapedParticle = {
  x: number; y: number;
  vx: number; vy: number;
  size: number;
  rgb: Rgb;
  alpha: number;
  life: number;
  decay: number;
  wobble: number;
  wobbleSpeed: number;
};

type NoiseFn = (x: number, y: number, z: number) => number;

type SimState = {
  noise: NoiseFn;
  particles: Particle[];
  morphOrder: number[];
  escaped: EscapedParticle[];
  rotY: number;
  time: number;
  breathPhase: number;
  warpCurrent: number;
  warpTarget: number;
  regenAccum: number;
  regenDelay: number;
  strokeEscapeCount: number;
  sliderRegenQueue: Particle[];
  sliderRegenAccum: number;
  mouseX: number | null;
  mouseY: number | null;
  prevMouseX: number | null;
  prevMouseY: number | null;
  isHovering: boolean;
  wasOverSphere: boolean;
  cfg: LevelConfig;
  pal: Palette;
  levelId: ResilienceLevel;
  colorblind: boolean;
  makeParticle: (index: number, isRegen: boolean, cfg: LevelConfig, pal: Palette) => Particle;
};

const R = 160;
const TARGET = 2700;
const LOW_REGEN_DELAY = 180;
const LOW_REGEN_RATE = 24 / 60;
const CANVAS_W = 500;
const CANVAS_H = 420;

const hexToRgb = (h: string): Rgb => [
  parseInt(h.slice(1, 3), 16),
  parseInt(h.slice(3, 5), 16),
  parseInt(h.slice(5, 7), 16),
];
const lerpRgb = (a: Rgb, b: Rgb, t: number): Rgb => [
  Math.round(a[0] + (b[0] - a[0]) * t),
  Math.round(a[1] + (b[1] - a[1]) * t),
  Math.round(a[2] + (b[2] - a[2]) * t),
];
const rgbStr = (c: Rgb) => `rgb(${c[0]},${c[1]},${c[2]})`;
const lerp1 = (a: number, b: number, t: number) => a + t * (b - a);
const randFrom = <T,>(a: T[]): T => a[Math.floor(Math.random() * a.length)]!;

function makeNoise(): NoiseFn {
  const perm = Array.from({ length: 512 }, () => Math.floor(Math.random() * 256));
  const fade = (t: number) => t * t * t * (t * (t * 6 - 15) + 10);
  const lerp = (a: number, b: number, t: number) => a + t * (b - a);
  const grad = (h: number, x: number, y: number, z: number) => {
    const g = h & 15;
    const u = g < 8 ? x : y;
    const v = g < 4 ? y : g === 12 || g === 14 ? x : z;
    return (g & 1 ? -u : u) + (g & 2 ? -v : v);
  };
  return (x, y, z) => {
    const X = Math.floor(x) & 255;
    const Y = Math.floor(y) & 255;
    const Z = Math.floor(z) & 255;
    x -= Math.floor(x); y -= Math.floor(y); z -= Math.floor(z);
    const u = fade(x), v = fade(y), w = fade(z);
    const A = perm[X]! + Y, AA = perm[A]! + Z, AB = perm[A + 1]! + Z;
    const B = perm[X + 1]! + Y, BA = perm[B]! + Z, BB = perm[B + 1]! + Z;
    return lerp(
      lerp(
        lerp(grad(perm[AA]!, x, y, z), grad(perm[BA]!, x - 1, y, z), u),
        lerp(grad(perm[AB]!, x, y - 1, z), grad(perm[BB]!, x - 1, y - 1, z), u),
        v,
      ),
      lerp(
        lerp(grad(perm[AA + 1]!, x, y, z - 1), grad(perm[BA + 1]!, x - 1, y, z - 1), u),
        lerp(grad(perm[AB + 1]!, x, y - 1, z - 1), grad(perm[BB + 1]!, x - 1, y - 1, z - 1), u),
        v,
      ),
      w,
    );
  };
}

function fibonacciSphere(n: number) {
  const pts: Array<{ nx: number; ny: number; nz: number }> = [];
  const phi = (1 + Math.sqrt(5)) / 2;
  for (let i = 0; i < n; i++) {
    const y = 1 - (2 * (i + 0.5)) / n;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = (2 * Math.PI * i) / phi;
    pts.push({ nx: r * Math.cos(theta), ny: y, nz: r * Math.sin(theta) });
  }
  return pts;
}

const FIB_SPHERE = fibonacciSphere(TARGET);

const ParticleSphere = ({
  level,
  interactive = true,
  showHint,
  colorblind = false,
  paused = false,
  zoom = 1,
  className,
}: ParticleSphereProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef<SimState | null>(null);
  const hintDismissedRef = useRef(false);
  const hintEnabled = showHint ?? interactive;

  const initState = useCallback(
    (levelId: ResilienceLevel, cbMode: boolean): SimState => {
      const noise = makeNoise();
      const cfg = LEVELS[levelId];
      const pal = cbMode ? PALETTES.colorblind : PALETTES.normal;

      const makeParticle = (
        index: number,
        isRegen: boolean,
        currentCfg: LevelConfig,
        currentPal: Palette,
      ): Particle => {
        const g = FIB_SPHERE[isRegen ? Math.floor(Math.random() * TARGET) : index % TARGET]!;
        const size = Math.max(
          0.4,
          currentCfg.sizeBase + (Math.random() - 0.5) * 2 * currentCfg.sizeVar,
        );
        const baseAlpha = Math.max(
          0.2,
          Math.min(1, currentCfg.alphaBase + (Math.random() - 0.5) * currentCfg.alphaVar),
        );
        const noiseOffset: [number, number, number] = [
          Math.random() * 100, Math.random() * 100, Math.random() * 100,
        ];
        const wobbleOffset: [number, number, number] = [
          Math.random() * 200, Math.random() * 200, Math.random() * 200,
        ];
        const driftOffset: [number, number, number] = [
          Math.random() * 300, Math.random() * 300, Math.random() * 300,
        ];
        const shimmerPhase = Math.atan2(g.nz, g.nx);
        const roll = Math.random();
        let type: 0 | 1 | 2;
        let redColor: Rgb | undefined;
        let greenColor: Rgb | undefined;
        let fixedColor: Rgb | undefined;
        if (roll < 0.6) {
          type = 0;
          redColor = hexToRgb(randFrom(currentPal.unhealthy));
          greenColor = hexToRgb(randFrom(currentPal.healthy));
        } else if (roll < 0.8) {
          type = 1;
          fixedColor = hexToRgb(randFrom(currentPal.accent));
        } else {
          type = 2;
          fixedColor = hexToRgb(randFrom(currentPal.neutral));
        }
        return {
          nx: g.nx, ny: g.ny, nz: g.nz,
          size, baseAlpha, alpha: baseAlpha,
          type, redColor, greenColor, fixedColor,
          currentT: 0, animT: 0,
          noiseOffset, wobbleOffset, driftOffset, shimmerPhase,
          dx: 0, dy: 0, vx: 0, vy: 0, partDx: 0, partDy: 0,
          escapeCooldown: 0, shed: false, regenScale: 1,
          instability: Math.random(),
        };
      };

      const particles = Array.from({ length: TARGET }, (_, i) =>
        makeParticle(i, false, cfg, pal),
      );

      const morphOrder: number[] = [];
      for (let i = 0; i < particles.length; i++) {
        if (particles[i]!.type === 0) morphOrder.push(i);
      }
      for (let i = morphOrder.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [morphOrder[i], morphOrder[j]] = [morphOrder[j]!, morphOrder[i]!];
      }
      const totalGreen = Math.round(cfg.greenFrac * morphOrder.length);
      for (let i = 0; i < morphOrder.length; i++) {
        const p = particles[morphOrder[i]!]!;
        p.currentT = i < totalGreen ? 1 : 0;
        p.animT = p.currentT;
      }

      return {
        noise, particles, morphOrder, escaped: [],
        rotY: 0.3, time: 0, breathPhase: 0,
        warpCurrent: 1 - cfg.value / 100, warpTarget: 1 - cfg.value / 100,
        regenAccum: 0, regenDelay: 0,
        strokeEscapeCount: 0,
        sliderRegenQueue: [], sliderRegenAccum: 0,
        mouseX: null, mouseY: null, prevMouseX: null, prevMouseY: null,
        isHovering: false, wasOverSphere: false,
        cfg, pal, levelId, colorblind: cbMode,
        makeParticle,
      };
    },
    [],
  );

  const switchLevel = useCallback(
    (newLevelId: ResilienceLevel, cbMode: boolean) => {
      const s = stateRef.current;
      if (!s) return;
      const newCfg = LEVELS[newLevelId];
      const newPal = cbMode ? PALETTES.colorblind : PALETTES.normal;
      const prevColorblind = s.colorblind;
      s.cfg = newCfg;
      s.pal = newPal;
      s.levelId = newLevelId;
      s.colorblind = cbMode;
      s.warpTarget = 1 - newCfg.value / 100;
      s.regenDelay = 0;

      const active = s.particles.filter(p => !p.shed && p.alpha > 0.01).length;
      const missing = TARGET - active;
      if (missing > 0) {
        s.sliderRegenQueue = [];
        s.sliderRegenAccum = 0;
        for (let i = 0; i < missing; i++) {
          const p = s.makeParticle(0, true, newCfg, newPal);
          p.regenScale = 0;
          if (p.type === 0) {
            p.currentT = Math.random() < newCfg.greenFrac ? 1 : 0;
            p.animT = p.currentT;
          }
          s.sliderRegenQueue.push(p);
        }
      }
      s.morphOrder = [];
      for (let i = 0; i < s.particles.length; i++) {
        const p = s.particles[i]!;
        if (p.type === 0 && !p.shed && p.alpha > 0) s.morphOrder.push(i);
      }
      for (let i = s.morphOrder.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [s.morphOrder[i], s.morphOrder[j]] = [s.morphOrder[j]!, s.morphOrder[i]!];
      }
      const tg = Math.round(newCfg.greenFrac * s.morphOrder.length);
      for (let i = 0; i < s.morphOrder.length; i++) {
        s.particles[s.morphOrder[i]!]!.currentT = i < tg ? 1 : 0;
      }

      if (cbMode !== prevColorblind) {
        for (const p of s.particles) {
          if (p.type === 0) {
            p.redColor = hexToRgb(randFrom(newPal.unhealthy));
            p.greenColor = hexToRgb(randFrom(newPal.healthy));
          } else if (p.type === 1) {
            p.fixedColor = hexToRgb(randFrom(newPal.accent));
          } else {
            p.fixedColor = hexToRgb(randFrom(newPal.neutral));
          }
        }
      }
    },
    [],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const cxc = CANVAS_W / 2;
    const cyc = CANVAS_H / 2;

    stateRef.current = initState(level, colorblind);
    let animId: number;

    const rotY2 = (x: number, z: number, a: number): [number, number] => [
      x * Math.cos(a) - z * Math.sin(a),
      x * Math.sin(a) + z * Math.cos(a),
    ];
    const rotX2 = (y: number, z: number, a: number): [number, number] => [
      y * Math.cos(a) - z * Math.sin(a),
      y * Math.sin(a) + z * Math.cos(a),
    ];
    const project = (px: number, py: number, pz: number, rotY: number) => {
      const [rx, rz1] = rotY2(px, pz, rotY);
      const [ry, rz2] = rotX2(py, rz1, 0.3);
      const sc = 600 / (600 + rz2 + R);
      return { sx: cxc + rx * sc, sy: cyc + ry * sc, depth: rz2, scale: sc };
    };

    const getRestPos = (p: Particle, s: SimState, breathScale: number): [number, number, number] => {
      const ws = s.cfg.warpSpeed;
      const n = s.noise(
        p.nx * 1.8 + p.noiseOffset[0] + s.time * ws * 1.8,
        p.ny * 1.8 + p.noiseOffset[1] + s.time * ws * 1.2,
        p.nz * 1.8 + p.noiseOffset[2] + s.time * ws * 0.9,
      );
      let r = R + n * s.warpCurrent * 68;
      if (s.cfg.warpAmp > 0) {
        const w = s.noise(
          p.nx * 3.5 + p.wobbleOffset[0] + s.time * ws * 5.5,
          p.ny * 3.5 + p.wobbleOffset[1] + s.time * ws * 4.2,
          p.nz * 3.5 + p.wobbleOffset[2] + s.time * ws * 3.8,
        );
        r += w * s.cfg.warpAmp * 32 * p.instability;
      }
      r *= breathScale;
      let ddx = 0;
      let ddy = 0;
      if (s.cfg.driftAmp > 0) {
        const ds = 0.8;
        ddx = s.noise(
          p.nx * ds + p.driftOffset[0] + s.time * ws * 2.2,
          p.ny * ds + p.driftOffset[1] + s.time * ws * 1.8,
          p.nz * ds + p.driftOffset[2] + s.time * ws * 1.4,
        ) * s.cfg.driftAmp;
        ddy = s.noise(
          p.nx * ds + p.driftOffset[0] + 50 + s.time * ws * 2.0,
          p.ny * ds + p.driftOffset[1] + 50 + s.time * ws * 1.6,
          p.nz * ds + p.driftOffset[2] + 50 + s.time * ws * 1.2,
        ) * s.cfg.driftAmp;
      }
      return [p.nx * r + ddx, p.ny * r + ddy, p.nz * r];
    };

    const spawnEscaped = (
      s: SimState, sx: number, sy: number, size: number, rgb: Rgb,
      bx: number, by: number, auto: boolean,
    ) => {
      const angle = auto
        ? Math.random() * Math.PI * 2
        : Math.atan2(by, bx) + (Math.random() - 0.5) * 0.8;
      const spd = auto ? 0.5 + Math.random() * 1.5 : 2 + Math.random() * 3;
      s.escaped.push({
        x: sx, y: sy,
        vx: Math.cos(angle) * spd,
        vy: Math.sin(angle) * spd - (auto ? 0 : Math.random() * 0.8),
        size: size * (0.5 + Math.random() * 0.8),
        rgb,
        alpha: 0.7 + Math.random() * 0.3,
        life: 1.0,
        decay: auto ? 0.006 + Math.random() * 0.008 : 0.008 + Math.random() * 0.012,
        wobble: Math.random() * Math.PI * 2,
        wobbleSpeed: (Math.random() - 0.5) * 0.08,
      });
      if (!auto) {
        s.strokeEscapeCount++;
        if (s.levelId === 'low') s.regenDelay = LOW_REGEN_DELAY;
      } else if (s.levelId === 'low') {
        s.regenDelay = Math.max(s.regenDelay, 60);
      }
    };

    const activeCount = (s: SimState) =>
      s.particles.filter(p => !p.shed && p.alpha > 0.01).length;

    const spawnRegen = (s: SimState) => {
      const p = s.makeParticle(0, true, s.cfg, s.pal);
      p.regenScale = 0;
      if (p.type === 0) {
        p.currentT = Math.random() < s.cfg.greenFrac ? 1 : 0;
        p.animT = p.currentT;
      }
      const idx = s.particles.length;
      s.particles.push(p);
      if (p.type === 0) s.morphOrder.push(idx);
    };

    const frame = () => {
      const s = stateRef.current;
      if (!s) {
        animId = requestAnimationFrame(frame);
        return;
      }

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      s.warpCurrent += (s.warpTarget - s.warpCurrent) * 0.04;
      s.time += 0.01;
      s.rotY += s.cfg.rotSpeed + (s.cfg.rotJitter > 0 ? (Math.random() - 0.5) * s.cfg.rotJitter * 2 : 0);
      s.breathPhase += s.cfg.breathSpeed * 0.012;
      const breathScale = s.cfg.breathAmp > 0 ? 1 + Math.sin(s.breathPhase) * s.cfg.breathAmp : 1;
      const shimmerWave = s.time * 1.8;
      const ph = s.cfg.physics;
      const redRatio = 1 - s.cfg.greenFrac;
      const detachThresh = lerp1(99999, 15, redRatio);

      if (s.sliderRegenQueue.length > 0) {
        s.sliderRegenAccum += Math.max(1, Math.ceil(s.sliderRegenQueue.length / 90));
        while (s.sliderRegenAccum >= 1 && s.sliderRegenQueue.length > 0) {
          s.sliderRegenAccum--;
          const p = s.sliderRegenQueue.shift()!;
          const idx = s.particles.length;
          s.particles.push(p);
          if (p.type === 0) s.morphOrder.push(idx);
        }
        if (s.sliderRegenQueue.length === 0) {
          s.morphOrder = [];
          for (let i = 0; i < s.particles.length; i++) {
            const p = s.particles[i]!;
            if (p.type === 0 && !p.shed && p.alpha > 0) s.morphOrder.push(i);
          }
          const tg = Math.round(s.cfg.greenFrac * s.morphOrder.length);
          for (let i = 0; i < s.morphOrder.length; i++) {
            s.particles[s.morphOrder[i]!]!.currentT = i < tg ? 1 : 0;
          }
        }
      }

      const ac = activeCount(s);
      if (ac < TARGET && s.sliderRegenQueue.length === 0) {
        if (s.levelId === 'low') {
          if (s.regenDelay > 0) {
            s.regenDelay--;
          } else {
            s.regenAccum += LOW_REGEN_RATE;
            while (s.regenAccum >= 1 && activeCount(s) < TARGET) {
              s.regenAccum--;
              spawnRegen(s);
            }
          }
        } else {
          s.regenAccum += lerp1(75 / 60, 750 / 60, s.cfg.value / 100);
          while (s.regenAccum >= 1 && activeCount(s) < TARGET) {
            s.regenAccum--;
            spawnRegen(s);
          }
        }
      }

      const proj = s.particles.map((p, i) => {
        const [px, py, pz] = getRestPos(p, s, breathScale);
        const pp = project(px, py, pz, s.rotY);
        return { i, rsx: pp.sx, rsy: pp.sy, depth: pp.depth, scale: pp.scale };
      });
      proj.sort((a, b) => a.depth - b.depth);

      const { mouseX, mouseY, prevMouseX, prevMouseY, isHovering } = s;
      let bx = 0;
      let by = 0;
      let bspeed = 0;
      let hasBrush = false;
      if (isHovering && mouseX !== null && prevMouseX !== null && mouseY !== null && prevMouseY !== null) {
        const dx = mouseX - prevMouseX;
        const dy = mouseY - prevMouseY;
        bspeed = Math.sqrt(dx * dx + dy * dy);
        if (bspeed > 0.5) {
          bx = dx / bspeed;
          by = dy / bspeed;
          hasBrush = true;
        }
      }

      const SPHERE_SCREEN_R = (R * 600) / (600 + R) + 8;
      if (mouseX !== null && mouseY !== null) {
        const overSphere =
          (mouseX - cxc) ** 2 + (mouseY - cyc) ** 2 < SPHERE_SCREEN_R ** 2;
        if (!overSphere && s.wasOverSphere) s.strokeEscapeCount = 0;
        s.wasOverSphere = overSphere;
      }

      if (s.cfg.autoShedRate > 0) {
        for (const pp of proj) {
          const p = s.particles[pp.i]!;
          if (p.shed || p.alpha <= 0 || p.escapeCooldown > 0) continue;
          if (p.instability > 0.82 && Math.random() < s.cfg.autoShedRate) {
            const rgb =
              p.type === 0 && p.redColor && p.greenColor
                ? lerpRgb(p.redColor, p.greenColor, p.animT)
                : p.fixedColor!;
            p.shed = true;
            p.vx += p.nx * 0.8;
            p.vy += p.ny * 0.5;
            spawnEscaped(s, pp.rsx, pp.rsy, p.size * pp.scale, rgb, 0, 0, true);
            p.escapeCooldown = 120;
          }
        }
      }

      if (isHovering && mouseX !== null && mouseY !== null) {
        const BRUSH_R = 40;
        const hasPrev =
          prevMouseX !== null && prevMouseY !== null &&
          (Math.abs(mouseX - prevMouseX) > 0.1 || Math.abs(mouseY - prevMouseY) > 0.1);
        const ax = hasPrev ? prevMouseX! : mouseX;
        const ay = hasPrev ? prevMouseY! : mouseY;
        const abx = mouseX - ax;
        const aby = mouseY - ay;
        const abLen2 = abx * abx + aby * aby;

        const distToSeg2 = (px: number, py: number) => {
          if (abLen2 < 0.001) return (px - ax) ** 2 + (py - ay) ** 2;
          const t = Math.max(
            0,
            Math.min(1, ((px - ax) * abx + (py - ay) * aby) / abLen2),
          );
          return (px - ax - t * abx) ** 2 + (py - ay - t * aby) ** 2;
        };

        const cands: Array<{ pp: (typeof proj)[number]; d2: number }> = [];
        for (const pp of proj) {
          const p = s.particles[pp.i]!;
          if (p.shed || p.alpha <= 0 || p.escapeCooldown > 0) continue;
          const sx = pp.rsx + p.dx;
          const sy = pp.rsy + p.dy;
          const d2 = distToSeg2(sx, sy);
          if (d2 < BRUSH_R * BRUSH_R) cands.push({ pp, d2 });
        }
        for (let i = cands.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [cands[i], cands[j]] = [cands[j]!, cands[i]!];
        }
        const budget = Math.max(1, Math.ceil(s.cfg.maxShed / 30));
        let shedThisFrame = 0;
        for (const { pp, d2 } of cands) {
          if (s.strokeEscapeCount >= s.cfg.maxShed || shedThisFrame >= budget) break;
          const p = s.particles[pp.i]!;
          const dist = Math.sqrt(d2);
          if ((1 - dist / BRUSH_R) ** 2 < 0.1) continue;
          const rgb =
            p.type === 0 && p.redColor && p.greenColor
              ? lerpRgb(p.redColor, p.greenColor, p.animT)
              : p.fixedColor!;
          p.shed = true;
          if (hasBrush) {
            p.vx += bx * 6 * ph.brushInfl;
            p.vy += by * 6 * ph.brushInfl;
          }
          spawnEscaped(
            s, pp.rsx + p.dx, pp.rsy + p.dy, p.size * pp.scale, rgb,
            hasBrush ? bx : Math.random() - 0.5,
            hasBrush ? by : Math.random() - 0.5,
            false,
          );
          p.escapeCooldown = 90;
          shedThisFrame++;
        }
      }

      for (const pp of proj) {
        const p = s.particles[pp.i]!;
        if (p.alpha <= 0) continue;
        if (p.regenScale < 1) p.regenScale = Math.min(1, p.regenScale + 0.025);
        if (p.shed) {
          p.vx *= 0.97;
          p.vy *= 0.97;
          p.vy -= 0.008;
          p.dx += p.vx;
          p.dy += p.vy;
          p.alpha = Math.max(0, p.alpha - 0.004);
          if (p.type === 0) p.animT += (p.currentT - p.animT) * 0.08;
          const rgb =
            p.type === 0 && p.redColor && p.greenColor
              ? lerpRgb(p.redColor, p.greenColor, p.animT)
              : p.fixedColor!;
          ctx.save();
          ctx.globalAlpha = p.alpha;
          ctx.fillStyle = rgbStr(rgb);
          ctx.beginPath();
          ctx.arc(
            pp.rsx + p.dx,
            pp.rsy + p.dy,
            Math.max(0.3, p.size * pp.scale * p.regenScale),
            0,
            Math.PI * 2,
          );
          ctx.fill();
          ctx.restore();
          continue;
        }
        if (p.escapeCooldown > 0) p.escapeCooldown--;
        if (isHovering && mouseX !== null && mouseY !== null && hasBrush) {
          const sx = pp.rsx + p.dx;
          const sy = pp.rsy + p.dy;
          const dist = Math.sqrt((sx - mouseX) ** 2 + (sy - mouseY) ** 2);
          if (dist < 40) {
            const falloff = (1 - dist / 40) ** 2;
            p.vx += bx * falloff * Math.min(bspeed, 30) * 1.4 * ph.brushInfl;
            p.vy += by * falloff * Math.min(bspeed, 30) * 1.4 * ph.brushInfl;
            p.partDx = lerp1(p.partDx, bx * ph.maxDisp * falloff, 0.25);
            p.partDy = lerp1(p.partDy, by * ph.maxDisp * falloff, 0.25);
          }
        }
        p.vx += (p.partDx - p.dx) * ph.spring;
        p.vy += (p.partDy - p.dy) * ph.spring;
        p.vx *= ph.damping;
        p.vy *= ph.damping;
        p.dx += p.vx;
        p.dy += p.vy;
        p.partDx *= ph.partDecay;
        p.partDy *= ph.partDecay;
        const dLen = Math.sqrt(p.dx * p.dx + p.dy * p.dy);
        if (dLen > ph.maxDisp) {
          p.dx = (p.dx / dLen) * ph.maxDisp;
          p.dy = (p.dy / dLen) * ph.maxDisp;
        }
        if (dLen > detachThresh && redRatio > 0.3) p.shed = true;
        if (p.type === 0) p.animT += (p.currentT - p.animT) * 0.08;
        const rgb =
          p.type === 0 && p.redColor && p.greenColor
            ? lerpRgb(p.redColor, p.greenColor, p.animT)
            : p.fixedColor!;
        const depthFade = (pp.depth + R) / (2 * R);
        const edgeSoft = lerp1(0.15, 0.55, s.warpCurrent);
        let shimmer = 1;
        if (s.cfg.shimmerStrength > 0) {
          shimmer = 1 + Math.sin(p.shimmerPhase * 2 - shimmerWave) * 0.18 * s.cfg.shimmerStrength;
        }
        ctx.save();
        ctx.globalAlpha = Math.min(1, p.alpha * (edgeSoft + (1 - edgeSoft) * depthFade) * shimmer);
        ctx.fillStyle = rgbStr(rgb);
        ctx.beginPath();
        ctx.arc(
          pp.rsx + p.dx,
          pp.rsy + p.dy,
          Math.max(0.3, p.size * pp.scale * p.regenScale),
          0,
          Math.PI * 2,
        );
        ctx.fill();
        ctx.restore();
      }

      for (let i = s.escaped.length - 1; i >= 0; i--) {
        const e = s.escaped[i]!;
        e.x += e.vx;
        e.y += e.vy;
        e.vx *= 0.98;
        e.vy *= 0.98;
        e.vy -= 0.012;
        e.wobble += e.wobbleSpeed;
        e.x += Math.sin(e.wobble) * 0.4;
        e.life -= e.decay;
        if (e.life <= 0) {
          s.escaped.splice(i, 1);
          continue;
        }
        ctx.save();
        ctx.globalAlpha = e.life * e.alpha;
        ctx.fillStyle = rgbStr(e.rgb);
        ctx.beginPath();
        ctx.arc(e.x, e.y, Math.max(0.3, e.size * (e.life * 0.7 + 0.3)), 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      if (s.particles.length > TARGET * 2) {
        s.particles = s.particles.filter(p => p.alpha > 0);
      }
      s.prevMouseX = s.mouseX;
      s.prevMouseY = s.mouseY;
      if (!paused) {
        animId = requestAnimationFrame(frame);
      }
    };

    const getPos = (e: MouseEvent | TouchEvent): [number, number] => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = CANVAS_W / rect.width;
      const scaleY = CANVAS_H / rect.height;
      const touch = (e as TouchEvent).touches?.[0];
      const clientX = touch ? touch.clientX : (e as MouseEvent).clientX;
      const clientY = touch ? touch.clientY : (e as MouseEvent).clientY;
      return [(clientX - rect.left) * scaleX, (clientY - rect.top) * scaleY];
    };

    const dismissHint = () => {
      if (hintDismissedRef.current) return;
      hintDismissedRef.current = true;
      canvas.dataset.hintDismissed = 'true';
    };

    const onMouseMove = (e: MouseEvent) => {
      dismissHint();
      const s = stateRef.current;
      if (!s) return;
      s.isHovering = true;
      [s.mouseX, s.mouseY] = getPos(e);
    };
    const onPointerEnd = () => {
      const s = stateRef.current;
      if (!s) return;
      s.isHovering = false;
      s.mouseX = null;
      s.mouseY = null;
      s.prevMouseX = null;
      s.prevMouseY = null;
      s.strokeEscapeCount = 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      dismissHint();
      const s = stateRef.current;
      if (!s) return;
      s.isHovering = true;
      [s.mouseX, s.mouseY] = getPos(e);
    };

    if (interactive) {
      canvas.addEventListener('mousemove', onMouseMove);
      canvas.addEventListener('mouseleave', onPointerEnd);
      canvas.addEventListener('touchmove', onTouchMove, { passive: false });
      canvas.addEventListener('touchend', onPointerEnd);
    }

    animId = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(animId);
      if (interactive) {
        canvas.removeEventListener('mousemove', onMouseMove);
        canvas.removeEventListener('mouseleave', onPointerEnd);
        canvas.removeEventListener('touchmove', onTouchMove);
        canvas.removeEventListener('touchend', onPointerEnd);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initState, interactive, paused]);

  useEffect(() => {
    if (stateRef.current) switchLevel(level, colorblind);
  }, [level, colorblind, switchLevel]);

  return (
    <div
      className={[styles.root, className].filter(Boolean).join(' ')}
      style={zoom !== 1 ? ({ '--ps-zoom': zoom } as CSSProperties) : undefined}
    >
      <canvas
        ref={canvasRef}
        width={CANVAS_W}
        height={CANVAS_H}
        className={[
          styles.canvas,
          interactive ? styles.canvasInteractive : '',
        ].filter(Boolean).join(' ')}
      />
      {hintEnabled && (
        <div className={styles.hint} aria-hidden='true'>
          <div className={styles.hintHandleTrack}>
            <div className={styles.hintHandleLine} />
            <div className={styles.hintHandle}>
              <img
                src={swipeHandIcon}
                alt=''
                aria-hidden='true'
                className={styles.hintHandleIcon}
              />
            </div>
          </div>
          <div className={styles.hintLabel}>Swipe to disrupt</div>
        </div>
      )}
    </div>
  );
};

export default ParticleSphere;
